import { extensions, ExtensionType, Texture } from "pixi.js";

/** Bucket host for this build. Each env file sets a different `REACT_APP_BUCKET_URL`. */
export function envBucketBase(): string | undefined {
  const bucket = process.env.REACT_APP_BUCKET_URL?.replace(/\/+$/, "");
  return bucket || undefined;
}

/**
 * True when `url` is an object in the bucket configured for this environment.
 * A photo from another environment's bucket does not match.
 */
export function isFloorPlanProfilePhotoUrl(url: string): boolean {
  const bucket = envBucketBase();
  if (!bucket) {
    return false;
  }
  return url === bucket || url.startsWith(`${bucket}/`);
}

/**
 * True when Pixi should load `url` as a profile image.
 * Bucket objects have no file extension, and neither do `/user-photos/…` media URLs.
 * Without this, the desk stays empty instead of showing the photo or falling back to initials.
 */
export function isPixiProfilePhotoUrl(url: string): boolean {
  if (isFloorPlanProfilePhotoUrl(url)) {
    return true;
  }
  return url.includes("/user-photos/");
}

/**
 * Address PlaceIcon can load for a profile photo.
 * Storage keys (`user-photos/…`) are prefixed with this environment's bucket.
 * An address that is already absolute is left unchanged.
 * A storage key is omitted when the bucket env is unset, so the desk can use initials.
 */
export function floorPlanPhotoUrl(photo: string): string | undefined {
  const trimmed = photo.trim();
  if (!trimmed || trimmed.length <= 2) {
    return undefined;
  }
  const isStorageKey = trimmed.startsWith("user-photos/") || trimmed.startsWith("/user-photos/");
  if (!isStorageKey) {
    return trimmed;
  }
  const bucket = envBucketBase();
  if (!bucket) {
    return undefined;
  }
  return `${bucket}/${trimmed.replace(/^\/+/, "")}`;
}

export const imageDelivery = {
  extension: ExtensionType.LoadParser,
  test: (url: string) => isPixiProfilePhotoUrl(url),
  async load(src: string) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(Texture.from(img));
      img.onerror = reject;
      img.src = src;
    });
  }
};

extensions.add(imageDelivery);
